import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from "react";
import { IRegraConciliacaoEmpresa } from "../../interfaces/IRegraConciliacaoEmpresa";
import regraConciliacaoService from "../../services/regraConciliacaoService";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { formatarCnpj, somenteDigitos } from "../../utils/String";
import Swal from 'sweetalert2';

type FiltroAtivo = 'ativos' | 'inativos';

function RegrasParametrizadas() {

    const history = useHistory();
    const { usuario } = useAuth();
    const { classes } = useLayout();
    const [dados, setDados] = useState<IRegraConciliacaoEmpresa[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filtroCliente, setFiltroCliente] = useState<string>('');
    const [filtroEmpresa, setFiltroEmpresa] = useState<string>('');
    const [filtroAtivo, setFiltroAtivo] = useState<FiltroAtivo>('ativos');

    const podeAlterarStatus = usuario?.perfil === 'Master';

    const carregarDados = useCallback(async (ativo: FiltroAtivo = filtroAtivo) => {
        setLoading(true);
        const result = await regraConciliacaoService.getDados(ativo === 'ativos');
        setDados(result);
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        carregarDados(filtroAtivo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroAtivo]);

    async function alterarStatus(item: IRegraConciliacaoEmpresa) {
        const acao = item.ativo ? 'Inativar' : 'Ativar';
        const swal = Swal.mixin({
            customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
            buttonsStyling: false
        });
        const result = await swal.fire({
            title: `${acao} Regra`,
            text: `Deseja realmente ${acao.toLowerCase()} esta regra de conciliação?`,
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            confirmButtonText: acao,
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        if (item.ativo) {
            await regraConciliacaoService.inativar(item.id, () => carregarDados(filtroAtivo));
        } else {
            await regraConciliacaoService.ativar(item.id, () => carregarDados(filtroAtivo));
        }
    }

    const dadosFiltrados = useMemo(() => {
        return dados.filter(item => {
            const matchCliente = !filtroCliente || item.nomeCliente.toLowerCase().includes(filtroCliente.toLowerCase());

            const termoEmpresa = filtroEmpresa.trim().toLowerCase();
            const digitosFiltro = somenteDigitos(filtroEmpresa);
            const matchEmpresa = !termoEmpresa
                || item.nomeEmpresa.toLowerCase().includes(termoEmpresa)
                || (digitosFiltro.length > 0 && somenteDigitos(item.cnpjEmpresa).includes(digitosFiltro));

            return matchCliente && matchEmpresa;
        });
    }, [dados, filtroCliente, filtroEmpresa]);

    return (
        <>
            <PageTitle breadcrumbs={[]}>Lista de Parametrizações</PageTitle>
            <div className='toolbar' id='kt_toolbar'>
                <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                    <DefaultTitle />
                </div>
            </div>

            <div className="card">
                <div className="card-header border-0 pt-6 flex-wrap gap-3">
                    <div className="card-title flex-wrap gap-3">
                        <input
                            type="text"
                            className="form-control form-control-solid w-200px"
                            placeholder="Filtrar por cliente"
                            value={filtroCliente}
                            onChange={e => setFiltroCliente(e.target.value)}
                        />
                        <input
                            type="text"
                            className="form-control form-control-solid w-200px"
                            placeholder="Filtrar por empresa"
                            value={filtroEmpresa}
                            onChange={e => setFiltroEmpresa(e.target.value)}
                        />
                        <div className="d-flex gap-2">
                            <button
                                className={`btn btn-sm ${filtroAtivo === 'ativos' ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => setFiltroAtivo('ativos')}
                            >
                                Ativas
                            </button>
                            <button
                                className={`btn btn-sm ${filtroAtivo === 'inativos' ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => setFiltroAtivo('inativos')}
                            >
                                Inativas
                            </button>
                        </div>
                        {(filtroCliente || filtroEmpresa) && !loading && (
                            <span className="text-muted fs-7 align-self-center">
                                {dadosFiltrados.length} resultado(s)
                            </span>
                        )}
                    </div>
                    <div className="card-toolbar">
                        <button
                            className="btn btn-primary"
                            onClick={() => history.push('/parametrizacoes/regra/conciliacao/formulario')}
                        >
                            + Adicionar Regra
                        </button>
                    </div>
                </div>

                <div className="card-body py-4">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-10">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4">
                                <thead>
                                    <tr className="fw-bold text-muted bg-light">
                                        <th className="min-w-140px ps-4 rounded-start">Cliente</th>
                                        <th className="min-w-140px">Empresa</th>
                                        <th className="min-w-160px">CNPJ</th>
                                        <th className="min-w-140px">Regra</th>
                                        <th className="min-w-180px">Contas</th>
                                        <th className="min-w-110px text-center">Somente essa regra</th>
                                        <th className="min-w-110px text-center">Executa no início</th>
                                        <th className="min-w-90px text-center">Status</th>
                                        <th className="min-w-140px text-end pe-4 rounded-end">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dadosFiltrados.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center text-muted py-8 fs-6">
                                                Nenhuma regra encontrada.
                                            </td>
                                        </tr>
                                    ) : (
                                        dadosFiltrados.map((item, index) => (
                                            <tr key={index}>
                                                <td className="ps-4">
                                                    <span className="text-dark fw-semibold d-block fs-6">{item.nomeCliente}</span>
                                                </td>
                                                <td>
                                                    <span className="text-dark fw-semibold d-block fs-6">{item.nomeEmpresa}</span>
                                                </td>
                                                <td>
                                                    <span className="text-muted fw-semibold d-block fs-7">{formatarCnpj(item.cnpjEmpresa)}</span>
                                                </td>
                                                <td>
                                                    <span className="text-dark fw-semibold d-block fs-6">{item.nomeRegra}</span>
                                                </td>
                                                <td>
                                                    <span
                                                        className="text-muted fw-semibold d-block fs-7"
                                                        style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        title={item.contas}
                                                    >
                                                        {item.contas}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge badge-light-${item.somenteEssaRegra ? 'success' : 'danger'} fs-7`}>
                                                        {item.somenteEssaRegra ? 'Sim' : 'Não'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge badge-light-${item.executarNoInicio ? 'success' : 'danger'} fs-7`}>
                                                        {item.executarNoInicio ? 'Sim' : 'Não'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge badge-light-${item.ativo ? 'success' : 'danger'} fs-7`}>
                                                        {item.ativo ? 'Ativa' : 'Inativa'}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-4">
                                                    <button
                                                        className="btn btn-sm btn-light-primary me-2"
                                                        onClick={() => history.push(`/parametrizacoes/regra/conciliacao/formulario/${item.id}`)}
                                                    >
                                                        Editar
                                                    </button>
                                                    {podeAlterarStatus && (
                                                        <button
                                                            className={`btn btn-sm ${item.ativo ? 'btn-light-danger' : 'btn-light-success'}`}
                                                            onClick={() => alterarStatus(item)}
                                                        >
                                                            {item.ativo ? 'Inativar' : 'Ativar'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default RegrasParametrizadas;
